<template>
    <v-card v-if="loggedUser" class="mb-4">
        <v-card-text>
            <h3 class="mb-5">{{ $t('editUserInformation.profileImage') }}</h3>
            <form>
                <v-text-field
                    :value="imageName"
                    label="Image"
                    readonly="readonly"
                    prepend-inner-icon="mdi-upload"
                    @click="pickImage"
                />
                <input
                    ref="image"
                    accept="image/jpeg,image/png"
                    name="image"
                    style="display: none"
                    type="file"
                    @change="setImage"
                />
                <div v-if="imageSrc">
                    <div
                        class="my-3 d-flex align-items-center justify-content-center mx-auto"
                    >
                        <vue-cropper
                            ref="cropper"
                            :aspectRatio="1"
                            :autoCrop="true"
                            :autoCropArea="1"
                            :guides="true"
                            :initialAspectRatio="1"
                            :src="imageSrc"
                            :viewMode="1"
                            class="mr-2 w-50"
                        />
                    </div>
                    <v-btn class="mr-2" small @click="cropImage">{{
                        $t('editUserInformation.imageUploadForm.crop')
                    }}</v-btn>
                    <v-btn class="mr-2" small @click="resetCrop">{{
                        $t('editUserInformation.imageUploadForm.reset')
                    }}</v-btn>
                    <v-btn small @click="resetImageData">{{
                        $t('editUserInformation.imageUploadForm.delete')
                    }}</v-btn>
                </div>
            </form>
            <v-img
                v-if="loggedUser.profileImageFilePath && !imageSrc"
                class="profile-image"
                :src="`${mediaUrl}/${loggedUser.profileImageFilePath}`"
            />
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                class="my-4"
                color="red"
                depressed
                :disabled="!(loggedUser.profileImageFilePath && !imageSrc)"
                @click="deleteProfileImage"
            >
                {{ $t('editUserInformation.imageUploadForm.remove') }}
            </v-btn>
            <v-btn
                class="ma-4"
                depressed
                :disabled="!imageSrc"
                @click="onImageUpload"
            >
                {{ $t('editUserInformation.imageUploadForm.submit') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes } from 'http-status-codes';
import { mediaUrl } from '@/config';

export default {
    name: 'profileImageForm',

    components: {
        VueCropper: () => import('vue-cropperjs')
    },

    data() {
        return {
            imageName: '',
            imageSrc: '',
            croppedImageSrc: '',
            mediaUrl
        };
    },

    computed: {
        ...mapGetters({
            loggedUser: 'auth/getLoggedUser'
        })
    },

    methods: {
        ...mapActions({
            storeProfileImage: 'auth/storeProfileImage',
            removeProfileImage: 'auth/removeProfileImage'
        }),

        pickImage() {
            this.$refs.image.click();
        },

        setImage(event) {
            const file = event.target.files[0];

            this.imageName = file.name;

            const reader = new FileReader();

            reader.onload = event => {
                this.imageSrc = event.target.result;

                if (this.$refs.cropper) {
                    this.$refs.cropper.replace(event.target.result);
                }
            };

            reader.readAsDataURL(file);
        },

        cropImage() {
            this.croppedImageSrc = this.$refs.cropper
                .getCroppedCanvas()
                .toDataURL();

            this.$refs.cropper.replace(this.croppedImageSrc);
        },

        async onImageUpload() {
            try {
                const blob = await new Promise(resolve =>
                    this.$refs.cropper.getCroppedCanvas().toBlob(resolve)
                );

                blob.name = this.imageName;

                await this.storeProfileImage({
                    image: blob
                });

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t(
                        'editUserInformation.imageUploadForm.imageUploadTitle'
                    ),
                    text: this.$t(
                        'editUserInformation.imageUploadForm.uploadSuccess'
                    )
                });

                this.resetImageData();
            } catch (error) {
                console.error(error);

                if (
                    error &&
                    error.response &&
                    error.response.status === StatusCodes.BAD_REQUEST
                ) {
                    return this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.$t('editUserInformation.fail.title'),
                        text: this.$t(
                            'editUserInformation.imageUploadForm.incorrectDimensions'
                        )
                    });
                }

                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('formCommonErrors.commonTitle'),
                    text: this.$t('formCommonErrors.serverError')
                });
            }
        },

        resetCrop() {
            this.$refs.cropper.reset();
            this.$refs.cropper.replace(this.imageSrc);
        },

        resetImageData() {
            this.imageName = '';
            this.imageSrc = '';
            this.croppedImageSrc = '';

            const input = this.$refs.image;
            input.type = 'text';
            input.type = 'file';
        },

        async deleteProfileImage() {
            try {
                await this.removeProfileImage();

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t(
                        'editUserInformation.imageUploadForm.imageUploadTitle'
                    ),
                    text: this.$t(
                        'editUserInformation.imageUploadForm.deleteSuccess'
                    )
                });
            } catch (err) {
                console.error(err);

                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('formCommonErrors.commonTitle'),
                    text: this.$t('formCommonErrors.serverError')
                });
            }
        }
    }
};
</script>
